import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MultiSortItem, PaginatedProps } from '../Components/CoreLib/library';
import { buildUpdatedSortingFromPaginationProps } from './sortingUtils';

/**
 * Reads sort tokens from the URL. Supports:
 * - Repeated keys: sorting=ORDER_DATE-&sorting=ORDER_NUMBER- (matches API / RTK query string)
 * - Legacy single param: sorting=ORDER_DATE-,ORDER_NUMBER-
 */
function sortingTokensFromSearchParams(searchParams: URLSearchParams, defaultPaginationProps: PaginatedProps): string[] {
    const raw = searchParams
        .getAll('sorting')
        .map((s) => s.trim())
        .filter(Boolean);
    if (raw.length === 0) {
        return buildUpdatedSortingFromPaginationProps(defaultPaginationProps);
    }
    if (raw.length === 1 && raw[0].includes(',')) {
        return raw[0]
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
    }
    return raw;
}

export interface UseListViewQueryParamsOptions {
    defaultPaginationProps: PaginatedProps;
}

export interface UseListViewQueryParamsResult {
    paginationProps: PaginatedProps;
    searchText: string;
    includeInactive: boolean;
    urlKey: string; // Used as key prop to force remount when URL params change
    syncPaginationToUrl: (props: PaginatedProps) => void;
    syncSearchToUrl: (search: string) => void;
    syncShowInactiveToUrl: (showInactive: boolean) => void;
    onPaginationChange: (pagination: PaginatedProps) => void;
    onSearchChange: (search: string) => void;
    onShowInactiveChange: (showInactive: boolean) => void;
}

/**
 * Hook to sync URL query parameters with list view state (sorting, pagination, search, filters)
 * Returns computed pagination props and other state from URL params
 */
export function useListViewQueryParams(
    options: UseListViewQueryParamsOptions
): UseListViewQueryParamsResult {
    const { defaultPaginationProps } = options;
    const [searchParams, setSearchParams] = useSearchParams();

    // Parse pagination props from URL
    const paginationProps = useMemo<PaginatedProps>(() => {
        const sortingArray = sortingTokensFromSearchParams(searchParams, defaultPaginationProps);
        const pageParam = searchParams.get('page');
        const page = pageParam !== null ? parseInt(pageParam, 10) : defaultPaginationProps.page;
        const pageSizeParam = searchParams.get('pageSize');
        const pageSize = pageSizeParam !== null ? parseInt(pageSizeParam, 10) : defaultPaginationProps.pageSize;

        const validUrlTokens = sortingArray.filter((item) => item.endsWith('+') || item.endsWith('-'));
        const sortTokens =
            validUrlTokens.length > 0 ? validUrlTokens : buildUpdatedSortingFromPaginationProps(defaultPaginationProps);
        const parsedSorting: MultiSortItem[] = sortTokens.map((item) => {
            const isAsc = item.endsWith('+');
            const key = item.slice(0, -1);
            return { key, direction: isAsc ? 'asc' : 'desc' } as MultiSortItem;
        });
        
        return {
            sorting: parsedSorting,
            page: isNaN(page) ? defaultPaginationProps.page : page,
            pageSize: isNaN(pageSize) ? defaultPaginationProps.pageSize : pageSize,
        };
    }, [searchParams, defaultPaginationProps]);

    // Parse search text from URL
    const searchText = useMemo(() => {
        return searchParams.get('search') || '';
    }, [searchParams]);

    // Parse includeInactive from URL
    const includeInactive = useMemo(() => {
        const includeInactiveParam = searchParams.get('includeInactive');
        return includeInactiveParam === 'true';
    }, [searchParams]);

    // Create a key based on URL params that change pagination/sorting/search
    // This will force StandardListViewV2 to remount when these params change
    const urlKey = useMemo(() => {
        const keyParts = [
            `sorting:${buildUpdatedSortingFromPaginationProps(paginationProps).join(',')}`,
            `page:${paginationProps.page}`,
            `pageSize:${paginationProps.pageSize}`,
            `search:${searchText}`,
            `includeInactive:${includeInactive}`,
        ];
        return keyParts.join('|');
    }, [paginationProps, searchText, includeInactive]);

    // Function to sync pagination props to URL
    const syncPaginationToUrl = useCallback((props: PaginatedProps) => {        
        setSearchParams((prev) => {
            // Create a new URLSearchParams to avoid mutation issues
            const newParams = new URLSearchParams(prev);
            
            newParams.set('page', props.page.toString());
            newParams.set('pageSize', props.pageSize.toString());
            newParams.delete('sorting');
            buildUpdatedSortingFromPaginationProps(props).forEach((token) => {
                if (token) {
                    newParams.append('sorting', token);
                }
            });
            return newParams;
        });
    }, [setSearchParams]);

    // Function to sync search text to URL
    const syncSearchToUrl = useCallback((search: string) => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            
            if (search && search.trim() !== '') {
                newParams.set('search', search);
            } else {
                newParams.delete('search');
            }
            return newParams;
        });
    }, [setSearchParams]);

    // Function to sync showInactive to URL
    const syncShowInactiveToUrl = useCallback((showInactive: boolean) => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            
            if (showInactive) {
                newParams.set('includeInactive', 'true');
            } else {
                newParams.delete('includeInactive');
            }
            return newParams;
        });
    }, [setSearchParams]);

    // Callback handlers for StandardListViewV2 listeners
    const onPaginationChange = useCallback((pagination: PaginatedProps) => {
        syncPaginationToUrl(pagination);
    }, [syncPaginationToUrl]);

    const onSearchChange = useCallback((search: string) => {
        syncSearchToUrl(search);
    }, [syncSearchToUrl]);

    const onShowInactiveChange = useCallback((showInactive: boolean) => {
        syncShowInactiveToUrl(showInactive);
    }, [syncShowInactiveToUrl]);

    return {
        paginationProps,
        searchText,
        includeInactive,
        urlKey,
        syncPaginationToUrl,
        syncSearchToUrl,
        syncShowInactiveToUrl,
        onPaginationChange,
        onSearchChange,
        onShowInactiveChange,
    };
}

